import { ConditionStatus } from '../vhc/model/types';
import ExpandBtn from './components/expand-btn';
import { checkGroups } from './model/constants';
import type { CheckItemType } from './model/types';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useGetVhcRecordQuery } from 'features/vhc/api/get-record';
import { useVhcRecordsReportEmailMutation } from 'features/vhc/api/use-vhc-records-report-email.mutation';
import { VhcItem } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { formatDate } from 'utils/formats';

export const ReportTab: React.FC = () => {
  const { id } = useParams();
  const [showReportError, setShowReportError] = useState<boolean>();
  const [showEmailError, setShowEmailError] = useState<string>();

  const { mutate: sendEmailMutate, isLoading: isLoadingSendEmail } = useVhcRecordsReportEmailMutation();
  const { data: vhcRecord, refetch } = useGetVhcRecordQuery(
    {
      id: Number(id)
    },
    undefined,
    {
      enabled: !!id
    }
  );

  useEffect(() => {
    if (vhcRecord?.generalData.dateComplete && showReportError) {
      setShowReportError(false);
    }
  }, [vhcRecord?.generalData.dateComplete]);

  const modifiedCheckGroups = useMemo(() => {
    const groups = checkGroups.map((g) => ({
      ...g,
      items: [] as CheckItemType[],
      statusCount: { ok: 0, warning: 0, notOk: 0, notTested: 0 }
    }));

    if (!vhcRecord?.items) return groups;

    vhcRecord.items.forEach((item) => {
      const targetGroup = groups.find((g) => g.groupId === item.group);
      if (!targetGroup) return;

      const status = (item.conditionId ?? ConditionStatus.NOT_TESTED) as ConditionStatus;

      targetGroup.items.push({
        id: item.id ?? 0,
        name: item.description || '',
        status,
        photos: item.imageUrls?.length,
        notes: item.notes || undefined,
        initialItem: item as VhcItem
      });

      switch (status) {
        case ConditionStatus.OK:
          targetGroup.statusCount.ok += 1;
          break;
        case ConditionStatus.WARNING:
          targetGroup.statusCount.warning += 1;
          break;
        case ConditionStatus.NOT_OK:
          targetGroup.statusCount.notOk += 1;
          break;
        case ConditionStatus.NOT_TESTED:
          targetGroup.statusCount.notTested += 1;
          break;
        default:
          break;
      }
    });

    return groups;
  }, [vhcRecord?.items]);

  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>(() => {
    const initialState: { [key: string]: boolean } = {};
    checkGroups.forEach((group, index) => {
      initialState[index] = index === 0;
    });
    return initialState;
  });

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const totals = useMemo(
    () => ({
      ok: modifiedCheckGroups.reduce((sum, group) => sum + group.statusCount.ok, 0),
      warning: modifiedCheckGroups.reduce((sum, group) => sum + group.statusCount.warning, 0),
      notOk: modifiedCheckGroups.reduce((sum, group) => sum + group.statusCount.notOk, 0)
    }),
    [modifiedCheckGroups]
  );

  const renderStatusBadge = (status: ConditionStatus) => {
    switch (status) {
      case ConditionStatus.OK:
        return <span className={clsx(styles.statusBadge, 'badge badge-light-success')}>OK</span>;
      case ConditionStatus.WARNING:
        return <span className={clsx(styles.statusBadge, 'badge badge-light-warning')}>Warning</span>;
      case ConditionStatus.NOT_OK:
        return <span className={clsx(styles.statusBadge, 'badge badge-light-danger')}>Not OK</span>;
      case ConditionStatus.NOT_TESTED:
        return <span className={clsx(styles.statusBadge, 'badge badge-light-secondary')}>Not Tested</span>;
      default:
        return <span className={clsx(styles.statusBadge, 'badge badge-light-secondary')}>-</span>;
    }
  };

  const { setRecordModalIsOpen } = useVhcStore();

  const handleViewItem = (item: VhcItem) => {
    setRecordModalIsOpen(
      {
        data: item,
        isReport: true,
        isComplete: Boolean(vhcRecord?.generalData.dateComplete) && Boolean(vhcRecord?.generalData.userComplete)
      },
      true
    );
  };

  const renderPhotoCount = (count: number | undefined) => {
    if (!count) return null;

    return (
      <div className="d-inline-flex align-items-center me-2">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            opacity="0.3"
            d="M14.05 0H5.95C2.66391 0 0 2.66391 0 5.95V14.05C0 17.3361 2.66391 20 5.95 20H14.05C17.3361 20 20 17.3361 20 14.05V5.95C20 2.66391 17.3361 0 14.05 0Z"
            fill="#3B74CE"
          />
          <path
            d="M5.79281 8.14844C5.39725 8.14844 5.01057 8.03114 4.68167 7.81138C4.35277 7.59162 4.09643 7.27926 3.94505 6.91381C3.79368 6.54835 3.75407 6.14622 3.83124 5.75826C3.90841 5.3703 4.09889 5.01393 4.3786 4.73422C4.6583 4.45452 5.01467 4.26404 5.40263 4.18687C5.79059 4.1097 6.19273 4.1493 6.55818 4.30068C6.92363 4.45205 7.23599 4.7084 7.45575 5.0373C7.67551 5.3662 7.79281 5.75288 7.79281 6.14844C7.79281 6.67887 7.5821 7.18758 7.20703 7.56265C6.83195 7.93772 6.32325 8.14844 5.79281 8.14844ZM20.0028 13.3884L17.7128 11.0784C17.4134 10.7729 17.0544 10.5321 16.6581 10.3712C16.2618 10.2102 15.8366 10.1325 15.4089 10.1428C14.9813 10.1531 14.5603 10.2513 14.1722 10.4311C13.7841 10.611 13.4371 10.8688 13.1528 11.1884L11.1528 13.3584C10.9544 13.5809 10.7137 13.7617 10.4447 13.8903C10.1758 14.0189 9.88401 14.0928 9.58628 14.1077C9.28854 14.1226 8.99082 14.0782 8.71041 13.977C8.42999 13.8758 8.17247 13.72 7.95281 13.5184C7.40023 13.0163 6.68634 12.7282 5.93999 12.7062C5.19365 12.6842 4.46402 12.9297 3.88281 13.3984L0.382812 16.2184C0.810261 17.3325 1.56598 18.2904 2.54992 18.9654C3.53385 19.6404 4.6996 20.0006 5.89281 19.9984H14.1028C15.6767 19.9722 17.1773 19.3285 18.281 18.2061C19.3846 17.0837 20.003 15.5726 20.0028 13.9984V13.3884Z"
            fill="#3B74CE"
          />
        </svg>

        <span className="ms-1">{count}</span>
      </div>
    );
  };

  const handleGenerateReport = () => {
    if (!vhcRecord?.generalData.dateComplete) {
      setShowReportError(true);

      setTimeout(() => {
        setShowReportError(false);
      }, 5000);

      return;
    }

    setShowReportError(false);
    window.open(`/external/vhc/report/${id}`, '_blank');
  };

  const canSaveEmail =
    !isLoadingSendEmail &&
    vhcRecord?.generalData.dateComplete &&
    !vhcRecord?.items?.some(
      (x) => x.conditionId === ConditionStatus.WARNING || x.conditionId === ConditionStatus.NOT_OK
    );

  const handleSendEmail = () => {
    setShowReportError(false);
    setShowEmailError(undefined);

    let hasError = false;

    if (!vhcRecord?.generalData.dateComplete) {
      setShowReportError(true);
      hasError = true;
    }

    if (
      vhcRecord?.items?.some(
        (x) => x.conditionId === ConditionStatus.WARNING || x.conditionId === ConditionStatus.NOT_OK
      )
    ) {
      setShowEmailError('Vhc record items is not Ok');
      hasError = true;
    }

    if (hasError) return;

    sendEmailMutate(
      {
        id: Number(id)
      },
      {
        onSuccess: () => {
          refetch();
        }
      }
    );
  };

  return (
    <div className={clsx(styles.container, showEmailError && styles.containerError)}>
      <div className={clsx(styles.buttonContainer)}>
        <div className={clsx(styles.printBtn)}>
          <button type="button" className={clsx('btn btn-light-secondary')} onClick={handleGenerateReport}>
            <InlineSvg path="/media/icons/custom/printer.svg" className="svg-icon-2" />
            PDF Report
          </button>
          {showReportError && <div className={clsx(styles.printReportError)}>VHC Record not Completed</div>}
        </div>
        <div className={clsx(styles.sendEmailBtn)}>
          <button
            type="button"
            className={clsx('btn btn-secondary')}
            onClick={handleSendEmail}
            disabled={isLoadingSendEmail}
          >
            {!isLoadingSendEmail ? (
              <>
                <InlineSvg path="/media/icons/custom/sms.svg" className="svg-icon-2" />
                Email Customer that all OK
              </>
            ) : (
              <span className="indicator-progress" style={{ display: 'block' }}>
                Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </button>
          {showEmailError && <div className={clsx(styles.printReportError)}>{showEmailError}</div>}
          {Boolean(vhcRecord?.generalData.dateSentReportEmail) && (
            <div className={styles.sentEmailInfo}>
              {vhcRecord?.generalData.userSentReportEmail ?? '-'}{' '}
              {vhcRecord?.generalData.dateSentReportEmail
                ? formatDate(vhcRecord?.generalData.dateSentReportEmail, 'DD MMM YYYY - HH:mm')
                : '-'}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {modifiedCheckGroups.map((group, index) => (
            <div key={index}>
              <ExpandBtn
                isExpanded={expandedSections[index]}
                toggleSection={() => toggleSection(index)}
                label={group.title || ''}
                group={group}
              />

              {expandedSections[index] && (
                <div className="p-4 border-bottom">
                  <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                      <thead className={clsx(styles.mobileTableHeader)}>
                        <tr className="fw-bold text-muted fs-6">
                          <th className="min-w-125px col-2">Description</th>
                          <th className="min-w-100px col-1">Condition</th>
                          <th className="min-w-150px col-1">Photos / Videos</th>
                          <th className="min-w-200px col ">Notes</th>
                          <th className="min-w-80px text-end col-1"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item) => (
                          <tr key={item.id}>
                            <td className="text-black fw-bold">
                              {item.name}
                              <div className="d-block d-md-none mt-4">
                                <span className="fw-medium fs-13 lh-19 ls-0 text-muted text-break">{item.notes}</span>
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <span className="d-inline-flex align-items-center gap-4 mt-4">
                                    {renderStatusBadge(item.status)}
                                    {renderPhotoCount(item.photos)}
                                  </span>
                                  <span>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleViewItem(item.initialItem)}
                                    >
                                      View
                                    </Button>
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="d-md-table-cell d-none">{renderStatusBadge(item.status)}</td>
                            <td className="d-md-table-cell d-none">{renderPhotoCount(item.photos)}</td>
                            <td className="d-md-table-cell d-none" style={{ maxWidth: 300 }}>
                              <span
                                className="text-truncate d-inline-block w-100"
                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {item.notes || '-'}
                              </span>
                            </td>
                            <td className="text-end d-md-table-cell d-none">
                              <Button variant="secondary" size="sm" onClick={() => handleViewItem(item.initialItem)}>
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 d-flex align-items-center mt-10">
        <div className="fw-bold fs-5 me-3">Total</div>
        <div className="d-flex align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <span>OK</span>
            <Badge bg="success" className="me-2  text-white">
              {' '}
              {totals.ok}
            </Badge>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span>Warning</span>
            <Badge bg="warning" className="me-2 text-white">
              {totals.warning}
            </Badge>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span>Not OK</span>
            <Badge bg="danger" className="text-white">
              {totals.notOk}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};
